import { useCallback, useEffect, useState } from 'react';
import { PrinterSettings, PrinterType, PrintFunction } from '~/types/printer';
import message from '~/utils/message';

export const useBluetoothPrinter = ({
  settings,
  saveSettings,
}: {
  settings: PrinterSettings;
  saveSettings: (settings: PrinterSettings) => void;
}) => {
  const [loading, setLoading] = useState(false);
  const [device, setDevice] = useState<any>(null);
  const [print, setPrint] = useState<((val: string) => Promise<void>) | null>(
    null
  );

  const handleBluetoothDisconnected = useCallback(() => {
    setDevice(null);
    setPrint(null);
    saveSettings({ type: PrinterType.Bluetooth });
  }, []);

  const connect = useCallback(async (device: any) => {
    await device.gatt.connect();

    const service = await device.gatt.getPrimaryService(0x18f0);
    const characteristic = await service.getCharacteristic(0x2af1);

    device.addEventListener(
      'gattserverdisconnected',
      handleBluetoothDisconnected
    );

    setDevice(device);
    setPrint(() => async (value: string) => {
      const encodedValue = base64ToUint8Array(value);
      const chunkSize = 200;
      for (let i = 0; i < encodedValue.length; i += chunkSize) {
        const chunk = encodedValue.slice(i, i + chunkSize);
        await characteristic.writeValue(chunk);
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
    });
    saveSettings({ type: PrinterType.Bluetooth, deviceId: device.id });
  }, []);

  const disconnect = useCallback(async () => {
    if (device) {
      device.removeEventListener(
        'gattserverdisconnected',
        handleBluetoothDisconnected
      );

      if (device.gatt.connected) {
        device.gatt.disconnect();
      }
    }

    handleBluetoothDisconnected();
  }, [device]);

  useEffect(() => {
    const init = async () => {
      if (settings?.type !== PrinterType.Bluetooth || !settings.deviceId) {
        return;
      }

      try {
        setLoading(true);
        const devices = await (window.navigator as any).bluetooth.getDevices();
        const device = devices.find((d: any) => d.id === settings.deviceId);

        if (!device) throw new Error('previously connected device not found');

        await connect(device);

        message.success('Success connect to bluetooth printer');
      } catch (error: any) {
        message.error('Failed to connect bluetooth printer: ' + error);
        saveSettings({ type: PrinterType.Bluetooth });
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const connectNew = useCallback(async () => {
    try {
      setLoading(true);
      const device = await (window.navigator as any).bluetooth.requestDevice({
        filters: [{ services: [0x18f0] }],
      });
      await connect(device);
    } catch (e) {
      message.error('Failed to connect device: ' + e);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    device,
    connect,
    connectNew,
    disconnect,
    print,
  };
};

function base64ToUint8Array(base64: string) {
  // Decode base64 string to raw binary string
  const binaryString = atob(base64);
  // Create a Uint8Array and fill it with char codes
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}
